import React, { useEffect, useMemo, useState } from 'react';
import { Container, Box, Button } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'react-simple-snackbar';
import styles from '../Clients/Clients.module.css';
import AddAccount from './AddAccount';
import { getAccountsByUser, deleteAccount } from '../../actions/accountActions';
import useAuthUser from '../../hooks/useAuthUser';

const Accounts = () => {
  const auth = useAuthUser()
  const dispatch = useDispatch()
  const { accounts, isLoading } = useSelector((state) => state.accounts)
  const [open, setOpen] = useState(false)
  const [currentId, setCurrentId] = useState(null)
  const [mobileDetailAccount, setMobileDetailAccount] = useState(null)
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false)
  const isMobile = useMediaQuery('(max-width:600px)')
  // eslint-disable-next-line no-unused-vars
  const [openSnackbar, closeSnackbar] = useSnackbar()

  useEffect(() => {
    const userId = auth?.result?._id || auth?.result?.googleId
    if (userId) {
      dispatch(getAccountsByUser({ search: userId }))
    }
  }, [auth, dispatch])

  const rows = useMemo(() => {
    return [...(accounts || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }, [accounts])

  const handleEdit = (id) => {
    setCurrentId(id)
    setOpen(true)
  }

  const handleDelete = (id) => {
    dispatch(deleteAccount(id, openSnackbar))
  }

  const openMobileDetail = (account) => {
    setMobileDetailAccount(account)
    if (mobileDetailOpen) return
    window.requestAnimationFrame(() => setMobileDetailOpen(true))
  }

  const closeMobileDetail = () => {
    setMobileDetailOpen(false)
  }

  const handleMobileDetailTransitionEnd = (event) => {
    if (event.target !== event.currentTarget) return
    if (!mobileDetailOpen) setMobileDetailAccount(null)
  }

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 180 },
    { field: 'billingAddress', headerName: 'Billing Address', flex: 1.2, minWidth: 180 },
    { field: 'billingCity', headerName: 'City', flex: 0.8, minWidth: 130 },
    { field: 'billingState', headerName: 'State', flex: 0.4, minWidth: 90 },
    { field: 'billingZip', headerName: 'Zip', flex: 0.5, minWidth: 100 },
    { field: 'taxIdNumber', headerName: 'Tax ID', flex: 0.8, minWidth: 140 },
    { field: 'term', headerName: 'Term (Days)', type: 'number', flex: 0.5, minWidth: 120 },
    { field: 'phone', headerName: 'Phone', flex: 0.8, minWidth: 140 },
    { field: 'email', headerName: 'Email', flex: 1.1, minWidth: 180 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            style={{color: 'white', backgroundColor: 'rgb(25, 118, 210)', marginRight: 10}}
            onClick={(e) => { e.stopPropagation(); handleEdit(params.row._id); }}
            variant="contained"
            size="small"
          >
            Edit
          </Button>
          <Button
            style={{color: 'white', backgroundColor: 'red'}}
            onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id); }}
            variant="contained"
            size="small"
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ]

  const mobileColumns = [
    { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 150 },
    { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 130 },
  ]

  return (
    <div className={styles.pageLayout}>
      <Container style={{ width: '90%', maxWidth: 1400 }}>
        <div className={styles.buttons}>
          <h1>Accounts</h1>
          <button
            className={styles.btn}
            onClick={() => { setCurrentId(null); setOpen((prev) => !prev) }}
          >
            Add Account
          </button>
        </div>
        {isMobile ? (
          <div className={styles.mobileGridWrap}>
            <div className={styles.mobileResultMeta}>
              <span>Tap a row for details</span>
            </div>
            <DataGrid
              autoHeight
              loading={isLoading}
              rows={rows}
              getRowId={(row) => row._id}
              columns={mobileColumns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
              sx={{
                border: '1px solid #dbe3ef',
                borderRadius: '12px',
                backgroundColor: '#fff',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc', minHeight: '42px !important' },
                '& .MuiDataGrid-columnHeaderTitle': { fontSize: '12px', fontWeight: 800 },
                '& .MuiDataGrid-cell': { py: '8px', fontSize: '13px', whiteSpace: 'normal', lineHeight: 1.25 },
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-toolbarContainer': { flexWrap: 'wrap', gap: '4px', p: '6px' },
                '& .MuiTablePagination-toolbar': { px: '8px' },
              }}
              onRowClick={(params) => openMobileDetail(params.row)}
            />
          </div>
        ) : (
          <DataGrid
            autoHeight
            loading={isLoading}
            rows={rows}
            getRowId={(row) => row._id}
            columns={columns}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
            sx={{
              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
            }}
            onRowDoubleClick={(params) => handleEdit(params.id)}
          />
        )}
        {mobileDetailAccount && (
          <aside
            className={`${styles.mobileDetailSidenav} ${mobileDetailOpen ? styles.mobileDetailSidenavOpen : ''}`}
            aria-label="Account details"
            onTransitionEnd={handleMobileDetailTransitionEnd}
          >
            <div className={styles.mobileDetailContent}>
              <div className={styles.mobileDetailTopBar}>
                <span>Account details</span>
                <Button size="small" color="primary" onClick={closeMobileDetail}>Close</Button>
              </div>
              <div className={styles.mobileDetailHeader}>
                <div>
                  <h2>{mobileDetailAccount.name || 'Account'}</h2>
                  <p>{mobileDetailAccount.phone || 'No phone'}</p>
                </div>
              </div>
              <div className={styles.mobileDetailGrid}>
                <div><span>Email</span><strong>{mobileDetailAccount.email || '—'}</strong></div>
                <div><span>Term</span><strong>{mobileDetailAccount.term ? `${mobileDetailAccount.term} days` : '—'}</strong></div>
                <div><span>Billing Address</span><strong>{mobileDetailAccount.billingAddress || '—'}</strong></div>
                <div><span>City</span><strong>{mobileDetailAccount.billingCity || '—'}</strong></div>
                <div><span>State</span><strong>{mobileDetailAccount.billingState || '—'}</strong></div>
                <div><span>Zip</span><strong>{mobileDetailAccount.billingZip || '—'}</strong></div>
                <div><span>Tax ID</span><strong>{mobileDetailAccount.taxIdNumber || '—'}</strong></div>
              </div>
              <div className={styles.mobileDrawerActions}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleEdit(mobileDetailAccount._id)
                    closeMobileDetail()
                  }}
                >
                  Edit
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    handleDelete(mobileDetailAccount._id)
                    closeMobileDetail()
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </aside>
        )}
      </Container>
      <AddAccount open={open} setOpen={setOpen} currentId={currentId} setCurrentId={setCurrentId} />
    </div>
  );
};

export default Accounts;
